/**
 * useSubstitutionRequests — convenience read hook (Phase 5).
 *
 * Read-only access to canonical substitution governance state for a job.
 * UI surfaces MUST consume this hook (or canonical readiness/procurement
 * signals) — never re-derive substitution truth inline.
 */
import { useMemo } from "react";
import { useVendorPO, type SubstitutionRequest } from "@/data/VendorPOContext";

export interface SubstitutionSummary {
  all: SubstitutionRequest[];
  pending: SubstitutionRequest[];
  approvedCompatible: SubstitutionRequest[];
  approvedConditional: SubstitutionRequest[];
  incompatibleOpen: SubstitutionRequest[];
  rejected: SubstitutionRequest[];
  superseded: SubstitutionRequest[];
}

export function useSubstitutionRequests(jobId: string | null | undefined): SubstitutionSummary {
  const { getSubstitutionRequestsByJob } = useVendorPO();
  return useMemo(() => {
    const all = jobId && typeof getSubstitutionRequestsByJob === "function" ? getSubstitutionRequestsByJob(jobId) : [];
    return {
      all,
      pending: all.filter((r) => r.status === "pending_review"),
      approvedCompatible: all.filter(
        (r) => r.status === "approved" && r.compatibilityAssessment === "compatible",
      ),
      approvedConditional: all.filter(
        (r) => r.status === "approved" && r.compatibilityAssessment === "conditional",
      ),
      incompatibleOpen: all.filter(
        (r) => r.compatibilityAssessment === "incompatible" && r.status !== "superseded",
      ),
      rejected: all.filter((r) => r.status === "rejected"),
      superseded: all.filter((r) => r.status === "superseded"),
    };
  }, [jobId, getSubstitutionRequestsByJob]);
}
